import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import ReactDOM from "react-dom";
import { usePortal } from "../../hooks/use-portal";
import "./searchable-async-select.scss";

export interface SearchableAsyncSelectOption {
  value: string;
  label: string;
}

export interface SearchableAsyncSelectProps {
  /** Unique id for the select, also used as the form field name. */
  id: string;
  /** Label shown above the input. */
  label?: string;
  /** Placeholder when nothing is selected or typed. */
  placeholder?: string;
  /** Mark the field as required (shows asterisk). */
  required?: boolean;
  /** Hint text below the input. */
  hint?: string;
  /** Error message — also applies error styling. */
  error?: string;
  /** Disable the component. */
  disabled?: boolean;

  /** Controlled selected value. */
  value?: string;
  /** Uncontrolled initial value. */
  defaultValue?: string;

  /**
   * Async callback fired after the user types at least `minSearchLength` characters.
   * Should return matching options from the API.
   */
  onSearch: (query: string) => Promise<SearchableAsyncSelectOption[]>;
  /** Called when the user selects an option. */
  onChange?: (option: SearchableAsyncSelectOption | null) => void;

  /** Minimum characters before triggering onSearch. @default 2 */
  minSearchLength?: number;
  /** Debounce delay in ms. @default 300 */
  debounceMs?: number;
  /** Message shown when search returns no results. @default "No results found" */
  noResultsMessage?: string;
  /** Message shown when fewer characters than minSearchLength are typed. */
  typeMoreMessage?: string;
  /** Static options shown before any search. */
  initialOptions?: SearchableAsyncSelectOption[];
  /** Allow the user to clear the selection. @default true */
  clearable?: boolean;
  /**
   * When true, the dropdown is rendered via `usePortal`.
   * Mounts into `targetRef` when provided, otherwise `document.body`.
   * @default false
   */
  usePortal?: boolean;
  /**
   * Portal mount element when `usePortal` is true. Coordinates are computed
   * relative to this container (`position: absolute`). The container should
   * have `position: relative`.
   */
  targetRef?: React.RefObject<HTMLElement | null>;
}

const DROPDOWN_STYLE_KEYS = [
  "position",
  "left",
  "top",
  "bottom",
  "width",
  "maxHeight",
  "zIndex",
] as const;

function isSameDropdownStyle(
  a: React.CSSProperties,
  b: React.CSSProperties
): boolean {
  return DROPDOWN_STYLE_KEYS.every((key) => a[key] === b[key]);
}

export function SearchableAsyncSelect(props: SearchableAsyncSelectProps) {
  const {
    id,
    label,
    placeholder = "Search...",
    required = false,
    hint,
    error,
    disabled = false,
    value: controlledValue,
    defaultValue,
    onSearch,
    onChange,
    minSearchLength = 2,
    debounceMs = 300,
    noResultsMessage = "No results found",
    typeMoreMessage,
    initialOptions = [],
    clearable = true,
    usePortal: shouldUsePortal = false,
    targetRef,
  } = props;

  const isControlled = controlledValue !== undefined;

  const [internalValue, setInternalValue] = useState<string>(
    defaultValue ?? ""
  );
  const selectedValue = isControlled ? controlledValue : internalValue;

  const [inputText, setInputText] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchableAsyncSelectOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const highlightSourceRef = useRef<"keyboard" | "mouse">("keyboard");

  const knownOptionsRef = useRef<Map<string, SearchableAsyncSelectOption>>(
    new Map()
  );

  for (const opt of initialOptions) {
    knownOptionsRef.current.set(opt.value, opt);
  }
  for (const opt of results) {
    knownOptionsRef.current.set(opt.value, opt);
  }

  const selectedOption = useMemo(() => {
    if (!selectedValue) return null;
    return knownOptionsRef.current.get(selectedValue) ?? null;
  }, [selectedValue, results, initialOptions]);

  // A query too short to search for yet shows the hint alone.
  const hasUnsearchableQuery =
    inputText.length > 0 && inputText.length < minSearchLength;

  // Never show one result set while waiting for another: in flight means no
  // rows, after a search means the latest results, before any search means
  // the seed options.
  const displayOptions =
    isLoading || hasUnsearchableQuery
      ? []
      : hasSearched
        ? results
        : initialOptions;

  const getPortalContainer = useCallback(
    () => targetRef?.current ?? null,
    [targetRef]
  );
  const renderInPortal = usePortal(getPortalContainer);

  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const applyDropdownStyle = useCallback((next: React.CSSProperties) => {
    setDropdownStyle((prev) => (isSameDropdownStyle(prev, next) ? prev : next));
  }, []);

  const computeDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;
    const triggerRect = containerRef.current.getBoundingClientRect();
    const gap = 4;
    const maxDropdown = 280;

    if (shouldUsePortal && targetRef?.current) {
      const containerRect = targetRef.current.getBoundingClientRect();
      const scrollTop = targetRef.current.scrollTop;
      const scrollLeft = targetRef.current.scrollLeft;
      const spaceBelow = containerRect.bottom - triggerRect.bottom - gap;
      const spaceAbove = triggerRect.top - containerRect.top - gap;
      const openAbove = spaceBelow < 260 && spaceAbove > spaceBelow;

      applyDropdownStyle({
        position: "absolute",
        left: triggerRect.left - containerRect.left + scrollLeft,
        width: triggerRect.width,
        zIndex: 99999,
        ...(openAbove
          ? {
              bottom:
                containerRect.bottom - triggerRect.top + scrollTop + gap,
              maxHeight: Math.min(spaceAbove - 8, maxDropdown),
            }
          : {
              top: triggerRect.bottom - containerRect.top + scrollTop + gap,
              maxHeight: Math.min(spaceBelow - 8, maxDropdown),
            }),
      });
      return;
    }

    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const openAbove = spaceBelow < 260 && triggerRect.top > spaceBelow;

    applyDropdownStyle({
      position: "fixed",
      left: triggerRect.left,
      width: triggerRect.width,
      zIndex: 99999,
      ...(openAbove
        ? {
            bottom: window.innerHeight - triggerRect.top + gap,
            maxHeight: Math.min(triggerRect.top - 8, maxDropdown),
          }
        : {
            top: triggerRect.bottom + gap,
            maxHeight: Math.min(spaceBelow - 8, maxDropdown),
          }),
    });
  }, [shouldUsePortal, targetRef, applyDropdownStyle]);

  useEffect(() => {
    if (!isOpen) return;
    computeDropdownPosition();
    const handleScroll = (e: Event) => {
      const target = e.target as Node | null;
      if (target && dropdownRef.current?.contains(target)) return;
      computeDropdownPosition();
    };
    const handleResize = () => computeDropdownPosition();
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen, computeDropdownPosition]);

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        closeDropdown();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setInputText("");
    setResults([]);
    setHasSearched(false);
    setIsLoading(false);
    setHighlightedIndex(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    requestIdRef.current++;
  }, []);

  const openDropdown = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    setInputText("");
    setResults([]);
    setHasSearched(false);
    setHighlightedIndex(-1);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [disabled]);

  const selectOption = useCallback(
    (option: SearchableAsyncSelectOption) => {
      knownOptionsRef.current.set(option.value, option);
      if (!isControlled) {
        setInternalValue(option.value);
      }
      onChange?.(option);
      closeDropdown();
    },
    [isControlled, onChange, closeDropdown]
  );

  const clearSelection = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!isControlled) {
        setInternalValue("");
      }
      onChange?.(null);
    },
    [isControlled, onChange]
  );

  const performSearch = useCallback(
    async (query: string) => {
      const currentRequestId = ++requestIdRef.current;
      setIsLoading(true);
      try {
        const data = await onSearch(query);
        if (currentRequestId === requestIdRef.current) {
          setResults(data);
          setHasSearched(true);
          setHighlightedIndex(-1);
        }
      } catch {
        if (currentRequestId === requestIdRef.current) {
          setResults([]);
          setHasSearched(true);
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [onSearch]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value;
      setInputText(query);

      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (query.length < minSearchLength) {
        setResults([]);
        setHasSearched(false);
        setIsLoading(false);
        requestIdRef.current++;
        return;
      }

      // Invalidate the previous list immediately so a stale in-flight
      // response cannot write rows for a query the user already moved past.
      requestIdRef.current++;
      setResults([]);
      setHasSearched(false);
      setHighlightedIndex(-1);
      setIsLoading(true);
      debounceRef.current = setTimeout(() => {
        performSearch(query);
      }, debounceMs);
    },
    [minSearchLength, debounceMs, performSearch]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openDropdown();
        }
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          highlightSourceRef.current = "keyboard";
          setHighlightedIndex((prev) =>
            prev < displayOptions.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          highlightSourceRef.current = "keyboard";
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : displayOptions.length - 1
          );
          break;
        case "Enter": {
          e.preventDefault();
          const highlighted = displayOptions[highlightedIndex];
          if (highlightedIndex >= 0 && highlighted) {
            selectOption(highlighted);
          }
          break;
        }
        case "Escape":
          e.preventDefault();
          closeDropdown();
          containerRef.current?.focus();
          break;
      }
    },
    [
      isOpen,
      openDropdown,
      closeDropdown,
      displayOptions,
      highlightedIndex,
      selectOption,
    ]
  );

  useEffect(() => {
    if (highlightedIndex < 0 || !dropdownRef.current) return;
    // Hovering already keeps the option in view, and scrolling the dropdown
    // here would retrigger the capture-phase scroll listener.
    if (highlightSourceRef.current === "mouse") return;
    const items = dropdownRef.current.querySelectorAll(
      ".sas-option"
    );
    items[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  const defaultTypeMoreMsg =
    typeMoreMessage ??
    `Type at least ${minSearchLength} character${minSearchLength > 1 ? "s" : ""} to search`;

  const showTypeMoreHint = isOpen && hasUnsearchableQuery;
  const showNoResults =
    isOpen && hasSearched && !isLoading && displayOptions.length === 0;
  const showLoading = isOpen && isLoading;

  const dropdown = (
    <div
      ref={dropdownRef}
      className="sas-dropdown"
      style={dropdownStyle}
      role="listbox"
      id={`${id}-listbox`}
    >
      {showTypeMoreHint && (
        <div className="sas-status">{defaultTypeMoreMsg}</div>
      )}
      {showLoading && (
        <div className="sas-status">
          <span className="sas-spinner" aria-label="Loading" />
          <span>Searching...</span>
        </div>
      )}
      {showNoResults && (
        <div className="sas-status">{noResultsMessage}</div>
      )}
      {!showLoading &&
        displayOptions.map((option, idx) => (
          <div
            key={option.value}
            id={`${id}-option-${idx}`}
            className={`sas-option${selectedValue === option.value ? " sas-option--selected" : ""}${idx === highlightedIndex ? " sas-option--highlighted" : ""}`}
            role="option"
            aria-selected={selectedValue === option.value}
            onClick={() => selectOption(option)}
            onMouseEnter={() => {
              highlightSourceRef.current = "mouse";
              setHighlightedIndex(idx);
            }}
          >
            <span className="sas-option-label">{option.label}</span>
            {selectedValue === option.value && <CheckIcon />}
          </div>
        ))}
      {!showLoading &&
        !showNoResults &&
        !showTypeMoreHint &&
        displayOptions.length === 0 &&
        inputText.length === 0 &&
        initialOptions.length === 0 && (
          <div className="sas-status">{defaultTypeMoreMsg}</div>
        )}
    </div>
  );

  return (
    <div
      className={`sas-container${disabled ? " sas-container--disabled" : ""}`}
    >
      {label && (
        <label className="sas-label" htmlFor={`${id}-input`}>
          {label}
          {required && <span className="sas-required"> *</span>}
        </label>
      )}

      <div
        ref={containerRef}
        className={`sas-trigger${isOpen ? " sas-trigger--open" : ""}${error ? " sas-trigger--error" : ""}`}
        onClick={() => !isOpen && openDropdown()}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-owns={`${id}-listbox`}
        onKeyDown={handleKeyDown}
      >
        {isOpen ? (
          <input
            ref={inputRef}
            id={`${id}-input`}
            className="sas-input"
            type="text"
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={selectedOption?.label ?? placeholder}
            autoComplete="off"
            aria-autocomplete="list"
            aria-controls={`${id}-listbox`}
            aria-activedescendant={
              highlightedIndex >= 0
                ? `${id}-option-${highlightedIndex}`
                : undefined
            }
          />
        ) : (
          <span
            className={
              selectedOption ? "sas-display-value" : "sas-placeholder"
            }
          >
            {selectedOption?.label ?? placeholder}
          </span>
        )}

        <span className="sas-icons">
          {isLoading && isOpen && (
            <span className="sas-spinner" aria-label="Loading" />
          )}
          {clearable && selectedOption && !isOpen && !disabled && (
            <button
              type="button"
              className="sas-clear-btn"
              aria-label="Clear selection"
              onClick={clearSelection}
              tabIndex={-1}
            >
              <ClearIcon />
            </button>
          )}
          <span className={`sas-chevron${isOpen ? " sas-chevron--open" : ""}`}>
            <ChevronIcon />
          </span>
        </span>
      </div>

      {hint && !error && <span className="sas-hint">{hint}</span>}
      {error && <span className="sas-error">{error}</span>}

      {isOpen &&
        (shouldUsePortal
          ? renderInPortal(dropdown)
          : ReactDOM.createPortal(dropdown, document.body))}
    </div>
  );
}

function ChevronIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M4 6l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M10.5 3.5L3.5 10.5M3.5 3.5l7 7"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M11.667 3.5L5.25 9.917 2.333 7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
